import React, { useState, forwardRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Lock } from "lucide-react";
import AuthService from "../../api/services/AuthService";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Slide,
  Box,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Snackbar, Alert } from "@mui/material";
import { Email } from "@mui/icons-material";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const SuccessDialog = ({ open, onClose }) => {
  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          backdropFilter: "blur(10px)",
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
          p: 2,
          textAlign: "center",
        },
      }}
    >
      <DialogTitle>
        <CheckCircleOutlineIcon
          color="success"
          sx={{ fontSize: 60, mb: 1 }}
        />
      </DialogTitle>
      <DialogContent>
        <Typography variant="h6" fontWeight="bold">
          réinitialiser mot de passe .
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Votre mot de passe a été modifié avec succès.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center" }}>
        <Button
          onClick={onClose}
          variant="contained"
          color="success"
          sx={{
            borderRadius: "10px",
            textTransform: "none",
            px: 3,
          }}
        >
          Continuer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default function ResetPassword() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const navigate = useNavigate();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [cguAccepted, setCguAccepted] = useState(false);
  const [showCguModal, setShowCguModal] = useState(false);
  useEffect(() => {
    const storedEmail = sessionStorage.getItem("resetEmail");
    if (storedEmail) {
      setEmail(storedEmail);
    }
  }, []);

  const handleReset = async (e) => {
    e.preventDefault();

    if (password !== confirm) {
      setSnackbarSeverity("error");
      setSnackbarMessage("Les mots de passe ne correspondent pas.");
      setSnackbarOpen(true);
      return;
    }

    if (!cguAccepted) {
      setSnackbarSeverity("error");
      setSnackbarMessage("Vous devez accepter les CGU pour continuer.");
      setSnackbarOpen(true);
      return;
    }

    try {
      await AuthService.resetPassword({ email, password, cgu_accepted: true });
      setSuccessDialogOpen(true);
    } catch (err) {
      console.error("Erreur backend :", err);

      const errorMsg = err.response?.data?.message || err.message || "Une erreur est survenue.";

      let translatedMsg = errorMsg;
      if (errorMsg.includes("The password field must be at least 8 characters")) {
        translatedMsg = "Le mot de passe doit contenir au moins 8 caractères.";
      } else if (errorMsg.includes("These credentials do not match our records")) {
        translatedMsg = "Les identifiants sont incorrects.";
      } else if (errorMsg.includes("User not found")) {
        translatedMsg = "Utilisateur introuvable.";
      } else if (errorMsg.includes("Token has expired")) {
        translatedMsg = "Le lien de réinitialisation a expiré.";
      }

      setSnackbarSeverity("error");
      setSnackbarMessage("Erreur : " + translatedMsg);
      setSnackbarOpen(true);
    }

  };

  const handleDialogClose = () => {
    setSuccessDialogOpen(false);
    sessionStorage.removeItem("resetEmail");
    navigate("/login");
  };

  return (
    <div style={styles.container}>
      <div style={styles.formContainer}>
        <div style={styles.header}>
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ mb: "16px" }}>
            <svg
              id="Calque_2"
              data-name="Calque 2"
              xmlns="http://www.w3.org/2000/svg"
              xmlnsXlink="http://www.w3.org/1999/xlink"
              viewBox="0 0 159.06 79.14"
              style={{ width: "120px", height: "auto" }}
            >
              <defs>
                <style>
                  {`
                    .cls-1 {
                      fill: url(#Dégradé_sans_nom_5);
                    }
                    .cls-2 {
                      fill: #1d1d1b;
                    }
                    .cls-3 {
                      fill: url(#Dégradé_sans_nom_5-2);
                    }
                  `}
                </style>
                <linearGradient
                  id="Dégradé_sans_nom_5"
                  data-name="Dégradé sans nom 5"
                  x1="16.83"
                  y1="46.58"
                  x2="1.64"
                  y2="46.4"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0" stopColor="#3269ab" />
                  <stop offset="1" stopColor="#8fc68b" />
                </linearGradient>
                <linearGradient
                  id="Dégradé_sans_nom_5-2"
                  data-name="Dégradé sans nom 5"
                  x1="19.11"
                  y1="39.57"
                  x2="65.06"
                  y2="39.57"
                  xlinkHref="#Dégradé_sans_nom_5"
                />
              </defs>
              <g id="Calque_1-2" data-name="Calque 1">
                <g>
                  <g>
                    <path
                      className="cls-2"
                      d="M54.87,63.37v2.67h7.99v7.85c-1.17.83-2.44,1.48-3.80,1.93-1.38.46-2.75.69-4.14.69-2.24,0-4.31-.56-6.21-1.64-1.91-1.10-3.41-2.58-4.52-4.45-1.10-1.87-1.65-3.93-1.65-6.17s.54-4.21,1.62-6.06c.01-.02.02-.03.03-.06,1.11-1.85,2.61-3.33,4.50-4.42,1.88-1.10,3.94-1.64,6.19-1.64h.03c1.65,0,3.24.29,4.75.89,1.53.59,2.88,1.45,4.07,2.56l1.67-2.04c-1.41-1.25-3.03-2.22-4.85-2.91-1.20-.46-2.45-.77-3.72-.91-.64-.09-1.30-.13-1.95-.13-2.76,0-5.32.66-7.67,1.98-2.34,1.32-4.19,3.10-5.55,5.34-.36.59-.66,1.19-.93,1.82-.74,1.75-1.11,3.61-1.11,5.59,0,2.68.68,5.17,2.04,7.45,1.36,2.27,3.19,4.07,5.52,5.40,2.31,1.33,4.83,2,7.59,2,1.93,0,3.85-.35,5.76-1.03,1.90-.67,3.55-1.61,4.96-2.80v-11.90h-10.60Z"
                    />
                    <path
                      className="cls-2"
                      d="M92.04,57.05v21.79h-2.83v-5.20c-.72,1.72-1.79,3.02-3.22,3.91-1.43.89-3.15,1.34-5.18,1.37-2.58,0-4.59-.75-6.05-2.27-1.45-1.51-2.18-3.58-2.18-6.22v-13.39h2.83v12.72c0,2.05.56,3.65,1.68,4.80s2.67,1.72,4.64,1.72c2.33-.03,4.16-.82,5.49-2.39,1.33-1.57,2-3.62,2-6.17v-10.69h2.83Z"
                    />
                    <path
                      className="cls-2"
                      d="M113.83,78.84l-.04-3.66c-.86,1.28-1.98,2.22-3.37,2.85-1.39.62-2.98.94-4.78.94-1.47,0-2.76-.28-3.89-.85-1.12-.57-1.99-1.35-2.60-2.35s-.91-2.12-.91-3.37c0-1.88.73-3.37,2.18-4.45,1.46-1.08,3.46-1.62,6.01-1.62h7.32v-1.66c0-1.72-.51-3.05-1.54-3.99-1.03-.94-2.52-1.41-4.49-1.41-2.33,0-4.67.87-7.03,2.62l-1.25-1.91c1.47-1.02,2.87-1.78,4.20-2.27,1.33-.48,2.85-.73,4.57-.73,2.63,0,4.67.66,6.11,1.97,1.44,1.32,2.17,3.14,2.20,5.47l.04,14.43h-2.74ZM111.06,75.28c1.34-.90,2.25-2.17,2.72-3.80v-2.99h-7.03c-1.91,0-3.37.32-4.37.96-1,.64-1.50,1.59-1.50,2.87s.48,2.35,1.43,3.14,2.22,1.18,3.80,1.18c1.94,0,3.58-.45,4.93-1.35Z"
                    />
                    <path
                      className="cls-2"
                      d="M129.90,58.32c1.34-.87,2.96-1.32,4.84-1.35v2.62c-2.30-.03-4.15.64-5.55,2.02-1.40,1.37-2.20,3.24-2.39,5.59v11.64h-2.79v-21.79h2.79v5.07c.72-1.66,1.75-2.93,3.10-3.80Z"
                    />
                    <path
                      className="cls-2"
                      d="M159.06,47.99v30.85h-2.79v-4.99c-.83,1.64-2,2.90-3.49,3.80-1.50.90-3.24,1.35-5.24,1.35s-3.88-.47-5.49-1.41c-1.61-.94-2.86-2.26-3.74-3.95-.89-1.69-1.33-3.60-1.33-5.74s.45-4.03,1.35-5.70c.90-1.66,2.15-2.97,3.74-3.91,1.59-.94,3.40-1.41,5.43-1.41s3.75.46,5.26,1.37c1.51.91,2.68,2.19,3.51,3.83v-14.09h2.79ZM152.29,75.49c1.25-.73,2.22-1.75,2.93-3.06.71-1.30,1.06-2.79,1.06-4.45s-.35-3.15-1.06-4.47c-.71-1.32-1.68-2.35-2.93-3.10-1.25-.75-2.66-1.12-4.24-1.12s-2.99.37-4.24,1.12c-1.25.75-2.23,1.78-2.95,3.10-.72,1.32-1.08,2.81-1.08,4.47s.35,3.15,1.06,4.45c.71,1.30,1.69,2.32,2.95,3.06,1.26.74,2.68,1.10,4.26,1.10s2.99-.37,4.24-1.10Z"
                    />
                  </g>
                  <path
                    className="cls-1"
                    d="M15.19,21.10v52.54c-3.25-1.82-6.23-4.08-8.86-6.68-1.03-1.02-2.01-2.08-2.93-3.20-.73-.88-1.43-1.80-2.08-2.74-.87-1.25-1.32-2.75-1.32-4.27V19.30c.59.60,1.17,1.20,1.76,1.80,1.12,0,2.24,0,3.36,0,.48-.46.96-.93,1.44-1.39.45.46.89.93,1.34,1.39,1.15,0,2.29,0,3.44,0,.44-.46.89-.92,1.33-1.38.47.45.93.91,1.40,1.36.37,0,.75,0,1.12.01Z"
                  />
                  <path
                    className="cls-2"
                    d="M15.15,0c-2.47,1.40-4.78,3.04-6.89,4.88h-.01c-.15.14-.28.26-.44.39-.05.02-.08.06-.12.11-1.54,1.38-2.97,2.89-4.28,4.48C2.16,11.36,1.03,12.95.01,14.60v.05h15.19V0h-.06Z"
                  />
                  <path
                    className="cls-3"
                    d="M43.67,0h-24.56v14.65h16.84c1.23-2.22,3.59-3.72,6.30-3.72,3.98,0,7.21,3.23,7.21,7.21s-3.23,7.21-7.21,7.21c-2.93,0-5.44-1.75-6.57-4.25h-16.57v56.31c1.23.38,2.50.68,3.78.93,1.02.20,2.05.37,3.10.50,1.66.20,3.36.30,5.07.30,1.33,0,2.65-.06,3.96-.18v-4.44h.02v-32.77h8.62c12.27,0,21.38-8.81,21.38-20.90S55.95,0,43.67,0Z"
                  />
                  <path
                    className="cls-2"
                    d="M44.87,16.72c2.45,1.93-1.18,5.32-3.01,2.59h-2.44v-1.99h2.44c.19-.13.26-.33.44-.49.69-.65,1.83-.69,2.57-.10Z"
                  />
                </g>
              </g>
            </svg>
          </Box>
          <p style={styles.subtitle}> Vous devez d’abord réinitialiser votre mot de passe avant de vous connecter.</p>
        </div>

        <form style={styles.formContent} onSubmit={handleReset}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Email</label>
            <div style={styles.inputContainer}>
              <Email style={styles.icon} />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={styles.input}
                placeholder="votre@email.com"
                required
              />
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Nouveau mot de passe</label>
            <div style={styles.inputContainer}>
              <Lock style={styles.icon} />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ ...styles.input, paddingRight: "48px" }}
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={styles.eyeButton}
              >
                {showPassword ? <EyeOff /> : <Eye />}
              </button>
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Confirmer le mot de passe</label>
            <div style={styles.inputContainer}>
              <Lock style={styles.icon} />
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                style={{ ...styles.input, paddingRight: "48px" }}
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={styles.eyeButton}
              >
                {showConfirmPassword ? <EyeOff /> : <Eye />}
              </button>
            </div>
          </div>
          {/* Case CGU */}
          <div style={styles.cguRow}>
            <input
              type="checkbox"
              id="cgu-reset-checkbox"
              checked={cguAccepted}
              onChange={(e) => setCguAccepted(e.target.checked)}
              style={styles.cguCheckbox}
            />
            <label htmlFor="cgu-reset-checkbox" style={styles.cguLabel}>
              Je reconnais avoir pris connaissance et accepter sans réserve les{" "}
              <button
                type="button"
                onClick={() => setShowCguModal(true)}
                style={styles.cguLink}
              >
                Conditions Générales d'Utilisation (CGU)
              </button>{" "}
              de la plateforme Guard.
            </label>
          </div>

          {/* Modal CGU */}
          {showCguModal && (
            <div style={styles.modalOverlay} onClick={() => setShowCguModal(false)}>
              <div style={styles.modalBox} onClick={(e) => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                  <span style={styles.modalTitle}>Conditions Générales d'Utilisation</span>
                  <span style={styles.versionTag}>v1.0</span>
                </div>
                <div style={styles.modalContent}>
                  <p><strong>Article 1 — Objet</strong><br />
                    La plateforme Guard est un service de gestion permettant aux gestionnaires
                    d'administrer les données de leurs sociétés et utilisateurs.
                  </p>
                  <p><strong>Article 2 — Accès</strong><br />
                    L'accès est réservé aux personnes autorisées disposant d'un compte valide.
                    Toute utilisation non autorisée est strictement interdite.
                  </p>
                  <p><strong>Article 3 — Responsabilités</strong><br />
                    L'utilisateur s'engage à utiliser la plateforme conformément à la
                    législation en vigueur et aux présentes CGU.
                  </p>
                  <p><strong>Article 4 — Données personnelles</strong><br />
                    Les données collectées sont traitées conformément à notre
                    Politique de confidentialité.
                  </p>
                </div>
                <button
                  type="button"
                  style={styles.modalAcceptBtn}
                  onClick={() => { setCguAccepted(true); setShowCguModal(false); }}
                >
                  ✓ J'accepte les CGU
                </button>
              </div>
            </div>
          )}

          <button type="submit" style={styles.submitButton}>
            Enregistrer
          </button>
        </form>
      </div>
      {/* Snackbar d'information */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        TransitionComponent={Slide}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <SuccessDialog
        open={successDialogOpen}
        onClose={handleDialogClose}
      />
    </div>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    background: `linear-gradient(90deg, 
    #2a5fa9 0%, 
    #3a7fb5 20%,
    #4a9fc0 40%, 
    #5ab8a0 60%,
    #70bd8d 80%, 
    #8fc68b 100%)`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
  },
  formContainer: {
    background: "rgba(255, 255, 255, 0.85)", backdropFilter: "blur(10px)",
    border: "1px solid rgba(255, 255, 255, 0.6)",
    borderRadius: "16px",
    padding: "24px",
    width: "100%",
    maxWidth: "420px",
    margin: "0 auto",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 20px 40px rgba(42, 95, 169, 0.1)",
      border: "1px solid rgba(42, 95, 169, 0.2)",
    },
  },
  header: {
    textAlign: "center",
    marginBottom: "32px",
  },
  subtitle: {
    color: "#6b7280",
    fontSize: "16px",
    fontWeight: "400",
  },
  formContent: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  inputGroup: {
    position: "relative",
  },
  label: {
    display: "block",
    fontSize: "14px",
    fontWeight: "500",
    color: "#374151",
    marginBottom: "8px",
  },
  inputContainer: {
    position: "relative",
  },
  input: {
    width: "100%",
    padding: "12px 16px 12px 44px",
    border: "1px solid #d1d5db",
    borderRadius: "10px",
    fontSize: "15px",
    background: "rgba(255, 255, 255, 0.7)",
    boxSizing: "border-box",
    outline: "none",
    transition: "border-color 0.3s, box-shadow 0.3s",
  },
  icon: {
    position: "absolute",
    left: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#6b7280",
    width: "20px",
    height: "20px",
  },
  eyeButton: {
    position: "absolute",
    right: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#6b7280",
    padding: "4px",
    display: "flex",
    alignItems: "center",
  },
  submitButton: {
    width: "100%",
    background: "linear-gradient(135deg, #2a5fa9 0%, #65b986 100%)",
    color: "white",
    fontWeight: "600",
    padding: "12px 24px",
    borderRadius: "10px",
    border: "none",
    cursor: "pointer",
    fontSize: "16px",
    boxShadow: "0 4px 12px rgba(42, 95, 169, 0.2)",
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  toggleSection: {
    textAlign: "center",
    marginTop: "16px",
  },
  toggleText: {
    color: "#6b7280",
    fontSize: "14px",
    marginBottom: "12px",
  },
  toggleButton: {
    background: "none",
    border: "none",
    color: "#2a5fa9",
    fontWeight: "600",
    cursor: "pointer",
    fontSize: "14px",
    textDecoration: "underline",
    transition: "color 0.3s",
  },
  cguRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    padding: "12px 14px",
    background: "rgba(42,95,169,0.05)",
    border: "1px solid rgba(42,95,169,0.15)",
    borderRadius: "8px",
  },
  cguCheckbox: {
    marginTop: "2px",
    width: "16px",
    height: "16px",
    flexShrink: 0,
    accentColor: "#2a5fa9",
    cursor: "pointer",
  },
  cguLabel: {
    fontSize: "13px",
    color: "#374151",
    lineHeight: "1.5",
    cursor: "pointer",
  },
  cguLink: {
    background: "none",
    border: "none",
    color: "#2a5fa9",
    fontWeight: "600",
    cursor: "pointer",
    fontSize: "13px",
    textDecoration: "underline",
    padding: 0,
  },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalBox: {
    background: "#fff",
    borderRadius: "14px",
    padding: "24px",
    width: "90%",
    maxWidth: "480px",
    maxHeight: "70vh",
    overflowY: "auto",
  },
  modalHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "16px",
  },
  modalTitle: {
    fontSize: "16px",
    fontWeight: "500",
    color: "#1d1d1b",
    flex: 1,
  },
  versionTag: {
    fontSize: "11px",
    background: "rgba(42,95,169,0.1)",
    color: "#2a5fa9",
    borderRadius: "4px",
    padding: "2px 6px",
  },
  modalContent: {
    fontSize: "13px",
    color: "#374151",
    lineHeight: "1.7",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  modalAcceptBtn: {
    marginTop: "16px",
    width: "100%",
    background: "linear-gradient(135deg, #2a5fa9 0%, #65b986 100%)",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "10px",
    fontWeight: "600",
    cursor: "pointer",
    fontSize: "14px",
  },
};